import { useState, useEffect } from 'react';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { RefreshCw, AlertTriangle, ShieldAlert } from 'lucide-react';

const fmtDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return isNaN(d) ? String(value).slice(0, 10) : d.toISOString().slice(0, 10);
};

export const TenantInventory = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/superadmin/companies/inventory');
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load the tenant inventory');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const companies = data?.companies || [];
  const orphans = data?.orphanedRows || {};
  const orphanTotal = Object.values(orphans).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6" data-testid="tenant-inventory">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between space-y-0">
          <div>
            <CardTitle>Tenant inventory</CardTitle>
            <CardDescription>
              Read-only. Every company in this database with the records attached to it, so you can
              tell real tenants from test ones before deleting anything.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            onClick={load}
            disabled={loading}
            data-testid="tenant-inventory-refresh"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {loading && !data ? (
            <p className="text-sm text-gray-500 py-8 text-center">Counting records…</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead className="text-right">Users</TableHead>
                    <TableHead className="text-right">Audits</TableHead>
                    <TableHead>Audit activity</TableHead>
                    <TableHead className="text-right">JSAs</TableHead>
                    <TableHead className="text-right">Incidents</TableHead>
                    <TableHead className="text-right">Corrective</TableHead>
                    <TableHead className="text-right">SDS</TableHead>
                    <TableHead className="text-right">Certs</TableHead>
                    <TableHead>Flags</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {companies.map((c) => (
                    <TableRow key={c.companyId} data-testid={`inventory-row-${c.companyId}`}>
                      <TableCell>
                        <div className="font-medium">{c.name || 'Unnamed'}</div>
                        <div className="text-xs text-gray-400 font-mono">{c.companyId}</div>
                        <div className="text-xs text-gray-500">created {fmtDate(c.createdAt)}</div>
                      </TableCell>
                      <TableCell className="text-right">{c.users}</TableCell>
                      <TableCell className="text-right font-semibold">{c.audits}</TableCell>
                      <TableCell className="text-xs text-gray-600 whitespace-nowrap">
                        {c.audits ? `${fmtDate(c.firstAuditDate)} → ${fmtDate(c.lastAuditDate)}` : '—'}
                      </TableCell>
                      <TableCell className="text-right">{c.jsas}</TableCell>
                      <TableCell className="text-right">{c.incidents}</TableCell>
                      <TableCell className="text-right">{c.correctiveActions}</TableCell>
                      <TableCell className="text-right">{c.sdsDocuments}</TableCell>
                      <TableCell className="text-right">{c.certificates}</TableCell>
                      <TableCell>
                        {c.quarantinedAudits > 0 && (
                          <Badge
                            variant="destructive"
                            className="whitespace-nowrap"
                            data-testid={`inventory-quarantine-${c.companyId}`}
                          >
                            <ShieldAlert className="w-3 h-3 mr-1" />
                            {c.quarantinedAudits} quarantined
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {companies.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center text-sm text-gray-500 py-6">
                        No companies found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {orphanTotal > 0 && (
        <Card className="border-amber-300 bg-amber-50" data-testid="orphaned-rows-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center text-amber-900">
              <AlertTriangle className="w-4 h-4 mr-2" />
              {orphanTotal} records already belong to no company
            </CardTitle>
            <CardDescription className="text-amber-800">
              Left behind by earlier deletions, before the delete covered every collection. They are
              unreachable in the app and will not be returned to any tenant.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.entries(orphans)
                .sort((a, b) => b[1] - a[1])
                .map(([collection, count]) => (
                  <Badge key={collection} variant="outline" className="bg-white">
                    {collection}: {count}
                  </Badge>
                ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TenantInventory;
